// Render-time conversion from SI base values to the user's selected unit
// system. Mirrors the @frontend/core metrics formatter (getMetricUnits +
// getProcessedValue) but specialised for the reporting table layout where
// the cell value and unit label are rendered separately.
//
// Single formatting rule for the whole app:
//  - `count`        → flat integer, no conversion          (e.g. "1", "20", "1,250")
//  - a real unit    → convert to the active system, 1dp    (e.g. "109.4")
//  - anything else  → 1dp, no conversion                   (e.g. "85.0", "19.0")
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import type { ReportingUnitType } from './sport-config/types'

export function getColumnUnitLabel(
  unitType: ReportingUnitType | undefined,
  unitSystem: UnitSystem
): string {
  // `count` is reporting-local and has no entry in `unitSystem.units`, so it
  // carries no abbreviation — the header shows the bare label.
  if (!unitType || unitType === 'count') return ''

  return unitSystem.units[unitType]?.abbreviation ?? ''
}

/**
 * Format an SI value for display per the app-wide rule above.
 *
 * `count` values are rounded to a flat integer (with locale thousands
 * separators) and are NOT converted. Real units convert to the active unit
 * system and render at one decimal place. Everything else (percentages,
 * per-minute rates, P90s, load/index values) renders at one decimal place
 * without conversion. Returns "-" for null/undefined/non-finite input.
 */
export function formatStatValue(
  siValue: number | null | undefined,
  unitType: ReportingUnitType | undefined,
  unitSystem: UnitSystem
): string {
  if (siValue == null || !Number.isFinite(siValue)) return '-'

  if (unitType === 'count') {
    return Math.round(siValue).toLocaleString('en-GB')
  }

  const conversion = unitType
    ? (unitSystem.units[unitType]?.conversion ?? 1)
    : 1

  return (siValue * conversion).toLocaleString('en-GB', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })
}
